import { emailHistoryApi, normalizeApiError } from './baseApi';

/**
 * Email History API Client - AI Insights
 * Uses the shared base API for standardized request handling
 */

/**
 * Build request body from filters, omitting null/empty values
 * @param {Object} filters - Filter parameters
 * @returns {Object} Request body
 */
function buildInsightRequestBody(filters) {
  const body = {};
  
  if (filters.dateFrom) body.dateFrom = filters.dateFrom;
  if (filters.dateTo) body.dateTo = filters.dateTo;
  if (filters.search && filters.search.trim()) body.search = filters.search.trim();
  if (filters.campaignId !== null && filters.campaignId !== undefined) {
    body.campaignId = filters.campaignId;
  }
  if (filters.bulkEmailId !== null && filters.bulkEmailId !== undefined) {
    body.bulkEmailId = filters.bulkEmailId;
  }
  
  return body;
}

/**
 * Generate AI insight for Campaigns tab
 * @param {Object} filters - Filter parameters
 * @param {string} [filters.dateFrom] - Filter by date from (ISO 8601)
 * @param {string} [filters.dateTo] - Filter by date to (ISO 8601)
 * @param {string} [filters.search] - Search filter
 * @returns {Promise<{insightType: string, headline: string, body: string, sentiment: string, metric?: Object}>}
 */
export async function generateCampaignInsight(filters = {}) {
  const requestBody = buildInsightRequestBody(filters);
  return emailHistoryApi.post('/insights/campaigns', requestBody);
}

/**
 * Generate AI insight for Bulk Emails tab
 * @param {Object} filters - Filter parameters
 * @param {string} [filters.dateFrom] - Filter by date from (ISO 8601)
 * @param {string} [filters.dateTo] - Filter by date to (ISO 8601)
 * @param {string} [filters.search] - Search filter
 * @param {number} [filters.campaignId] - Filter by campaign ID
 * @returns {Promise<{insightType: string, headline: string, body: string, sentiment: string, metric?: Object}>}
 */
export async function generateBulkEmailInsight(filters = {}) {
  const requestBody = buildInsightRequestBody(filters);
  return emailHistoryApi.post('/insights/bulk', requestBody);
}

/**
 * Generate AI insight for Individual Emails tab
 * @param {Object} filters - Filter parameters
 * @param {string} [filters.dateFrom] - Filter by date from (ISO 8601)
 * @param {string} [filters.dateTo] - Filter by date to (ISO 8601)
 * @param {string} [filters.search] - Search filter
 * @param {number} [filters.bulkEmailId] - Filter by bulk email ID
 * @returns {Promise<{insightType: string, headline: string, body: string, sentiment: string, metric?: Object}>}
 */
export async function generateIndividualEmailInsight(filters = {}) {
  const requestBody = buildInsightRequestBody(filters);
  return emailHistoryApi.post('/insights/individual', requestBody);
}

/**
 * Generate AI insight for Scheduled tab
 * @param {Object} filters - Filter parameters
 * @param {string} [filters.dateFrom] - Filter by date from (ISO 8601)
 * @param {string} [filters.dateTo] - Filter by date to (ISO 8601)
 * @param {string} [filters.search] - Search filter
 * @param {number} [filters.campaignId] - Filter by campaign ID
 * @returns {Promise<{insightType: string, headline: string, body: string, sentiment: string, metric?: Object}>}
 */
export async function generateScheduledInsight(filters = {}) {
  const requestBody = buildInsightRequestBody(filters);
  return emailHistoryApi.post('/insights/scheduled', requestBody);
}

